// ─────────────────────────────────────────────────────────────────────────────
// forecast_series tool — client-side time-series projection
//
// Methods:
//   • linear     — least-squares regression. Best for clean monotonic trends.
//   • exp_smooth — Holt's linear-trend double exponential smoothing. Better
//                  when the series has noise but follows a trend.
//   • auto       — fits both, picks whichever has lower in-sample MAPE on the
//                  last 20% of points (held back as validation when N >= 8).
//
// Confidence band: ±1.96 × residual standard deviation (≈95% prediction interval).
// All math is pure JS — no new deps.
// ─────────────────────────────────────────────────────────────────────────────

import { widgetRegistry } from "../registry/widgetRegistry";
import type { RegisteredWidget, WidgetSeries } from "../registry/types";

export type ForecastMethod = "auto" | "linear" | "exp_smoothing";

export interface ForecastInput {
	chart_id?: string;
	chart_title?: string;
	series_name?: string;
	horizon: number;
	method?: ForecastMethod;
}

export interface ForecastPoint {
	x: string | number;
	y: number;
	lower: number;
	upper: number;
}

export interface ForecastOutput {
	found: boolean;
	widget_id?: string;
	widget_title?: string;
	series_name?: string;
	method_used?: ForecastMethod;
	predicted?: ForecastPoint[];
	historical?: Array<{ x: string | number; y: number }>;
	trend?: "increasing" | "decreasing" | "stable";
	r_squared?: number;
	mape?: number;
	message?: string;
	warnings?: string[];
}

// ── Series resolution ────────────────────────────────────────────────────────

function findWidget(input: ForecastInput): RegisteredWidget | undefined {
	if (input.chart_id) {
		const w = widgetRegistry.get(input.chart_id);
		if (w) return w;
	}
	if (input.chart_title) {
		const t = input.chart_title.toLowerCase().trim();
		const all = widgetRegistry.list();
		const exact = all.find((w) => w.title.toLowerCase() === t);
		if (exact) return exact;
		return all.find((w) => w.title.toLowerCase().includes(t));
	}
	return undefined;
}

function pickSeries(widget: RegisteredWidget, name?: string): WidgetSeries | undefined {
	if (!widget.series || widget.series.length === 0) return undefined;
	if (name) {
		const exact = widget.series.find((s) => s.name === name);
		if (exact) return exact;
		const fuzzy = widget.series.find((s) =>
			s.name.toLowerCase().includes(name.toLowerCase())
		);
		if (fuzzy) return fuzzy;
	}
	// Default: first series with at least 3 numeric points
	for (const s of widget.series) {
		const numericPoints = s.data.filter((p) => typeof p.y === "number" && Number.isFinite(p.y));
		if (numericPoints.length >= 3) return s;
	}
	return widget.series[0];
}

// ── Math helpers ──────────────────────────────────────────────────────────────

function mean(arr: number[]): number {
	return arr.reduce((a, b) => a + b, 0) / arr.length;
}

function residualStdDev(actual: number[], predicted: number[]): number {
	if (actual.length === 0) return 0;
	let sumSq = 0;
	for (let i = 0; i < actual.length; i++) {
		const r = actual[i] - predicted[i];
		sumSq += r * r;
	}
	return Math.sqrt(sumSq / Math.max(1, actual.length - 1));
}

function mapePct(actual: number[], predicted: number[]): number {
	let total = 0;
	let count = 0;
	for (let i = 0; i < actual.length; i++) {
		if (actual[i] === 0) continue;
		total += Math.abs((actual[i] - predicted[i]) / actual[i]);
		count++;
	}
	return count === 0 ? Infinity : (total / count) * 100;
}

// ── Linear regression ────────────────────────────────────────────────────────

interface LinearFit {
	slope: number;
	intercept: number;
	rSquared: number;
	residualStd: number;
	predicted: number[];
}

function linearRegression(ys: number[]): LinearFit {
	const n = ys.length;
	const xs = Array.from({ length: n }, (_, i) => i);
	const xBar = mean(xs);
	const yBar = mean(ys);
	let num = 0;
	let den = 0;
	for (let i = 0; i < n; i++) {
		num += (xs[i] - xBar) * (ys[i] - yBar);
		den += (xs[i] - xBar) ** 2;
	}
	const slope = den === 0 ? 0 : num / den;
	const intercept = yBar - slope * xBar;

	const predicted = xs.map((x) => slope * x + intercept);

	let ssRes = 0;
	let ssTot = 0;
	for (let i = 0; i < n; i++) {
		ssRes += (ys[i] - predicted[i]) ** 2;
		ssTot += (ys[i] - yBar) ** 2;
	}
	const rSquared = ssTot === 0 ? 1 : Math.max(0, 1 - ssRes / ssTot);
	const residualStd = residualStdDev(ys, predicted);
	return { slope, intercept, rSquared, residualStd, predicted };
}

function forecastLinear(ys: number[], horizon: number) {
	const fit = linearRegression(ys);
	const n = ys.length;
	const projected = [];
	for (let h = 1; h <= horizon; h++) {
		const yHat = fit.slope * (n - 1 + h) + fit.intercept;
		const band = 1.96 * fit.residualStd;
		projected.push({ y: yHat, lower: yHat - band, upper: yHat + band });
	}
	return { fit, projected };
}

// ── Holt's linear-trend exponential smoothing ────────────────────────────────

function forecastExpSmoothing(
	ys: number[],
	horizon: number,
	alpha = 0.45,
	beta = 0.25
) {
	const n = ys.length;
	if (n < 2) {
		const lastY = n === 1 ? ys[0] : 0;
		return {
			predicted: ys.slice(),
			projected: Array.from({ length: horizon }, () => ({
				y: lastY,
				lower: lastY,
				upper: lastY,
			})),
			residualStd: 0,
		};
	}

	let level = ys[0];
	let trend = ys[1] - ys[0];
	const inSample: number[] = [ys[0]];

	for (let i = 1; i < n; i++) {
		const prevLevel = level;
		const prevTrend = trend;
		level = alpha * ys[i] + (1 - alpha) * (prevLevel + prevTrend);
		trend = beta * (level - prevLevel) + (1 - beta) * prevTrend;
		inSample.push(prevLevel + prevTrend);
	}

	const residualStd = residualStdDev(ys, inSample);
	const projected = [];
	for (let h = 1; h <= horizon; h++) {
		const yHat = level + h * trend;
		// Widen band over horizon (uncertainty grows with distance)
		const band = 1.96 * residualStd * Math.sqrt(h);
		projected.push({ y: yHat, lower: yHat - band, upper: yHat + band });
	}
	return { predicted: inSample, projected, residualStd };
}

// ── Method selection ─────────────────────────────────────────────────────────

function autoSelectMethod(ys: number[]): {
	method: ForecastMethod;
	mape: number;
} {
	if (ys.length < 8) {
		// Too few points to validate — go with linear (more robust on small N)
		return { method: "linear", mape: 0 };
	}
	const cut = Math.floor(ys.length * 0.8);
	const train = ys.slice(0, cut);
	const test = ys.slice(cut);

	// Linear hold-out
	const linFit = linearRegression(train);
	const linPredOnTest = test.map(
		(_, i) => linFit.slope * (cut + i) + linFit.intercept
	);
	const linMape = mapePct(test, linPredOnTest);

	// Exp-smoothing hold-out
	const esResult = forecastExpSmoothing(train, test.length);
	const esPredOnTest = esResult.projected.map((p) => p.y);
	const esMape = mapePct(test, esPredOnTest);

	if (linMape <= esMape) return { method: "linear", mape: linMape };
	return { method: "exp_smoothing", mape: esMape };
}

// ── Public entry point ───────────────────────────────────────────────────────

export function forecastSeries(input: ForecastInput): ForecastOutput {
	const horizon = Math.max(1, Math.min(24, Math.floor(input.horizon ?? 4)));
	const widget = findWidget(input);
	if (!widget) {
		return {
			found: false,
			message:
				"Widget not found. Pass chart_id from the registered-widgets index, or chart_title for fuzzy match.",
		};
	}

	const series = pickSeries(widget, input.series_name);
	if (!series) {
		return {
			found: false,
			widget_id: widget.widgetId,
			widget_title: widget.title,
			message: `Widget "${widget.title}" has no time-series data to forecast.`,
		};
	}

	const numericPoints = series.data
		.map((p) => ({ x: p.x, y: typeof p.y === "number" ? p.y : Number(p.y) }))
		.filter((p) => Number.isFinite(p.y));

	if (numericPoints.length < 3) {
		return {
			found: false,
			widget_id: widget.widgetId,
			widget_title: widget.title,
			series_name: series.name,
			message: `Series "${series.name}" has only ${numericPoints.length} numeric point(s). Need at least 3 to forecast.`,
		};
	}

	const ys = numericPoints.map((p) => p.y);
	const requested = input.method ?? "auto";
	const warnings: string[] = [];
	let methodUsed: ForecastMethod;
	let mape: number | undefined;

	if (requested === "auto") {
		const sel = autoSelectMethod(ys);
		methodUsed = sel.method;
		mape = sel.mape;
	} else {
		methodUsed = requested;
	}

	let projected: Array<{ y: number; lower: number; upper: number }>;
	let rSquared: number | undefined;

	if (methodUsed === "linear") {
		const r = forecastLinear(ys, horizon);
		projected = r.projected;
		rSquared = r.fit.rSquared;
	} else {
		const r = forecastExpSmoothing(ys, horizon);
		projected = r.projected;
	}

	if (numericPoints.length < 6) {
		warnings.push(`Only ${numericPoints.length} historical points — forecast confidence is low.`);
	}

	const lastX = numericPoints[numericPoints.length - 1].x;
	const stepGuess = inferStep(numericPoints.map((p) => p.x));
	const predicted: ForecastPoint[] = projected.map((p, i) => ({
		x: extendX(lastX, stepGuess, i + 1),
		y: round(p.y),
		lower: round(p.lower),
		upper: round(p.upper),
	}));

	const trendDir =
		methodUsed === "linear"
			? trendFromSlope(ys, projected)
			: trendFromEndpoints(numericPoints[numericPoints.length - 1].y, projected[0].y);

	return {
		found: true,
		widget_id: widget.widgetId,
		widget_title: widget.title,
		series_name: series.name,
		method_used: methodUsed,
		predicted,
		historical: numericPoints,
		trend: trendDir,
		r_squared: rSquared !== undefined ? round(rSquared, 4) : undefined,
		mape: mape !== undefined && Number.isFinite(mape) ? round(mape, 2) : undefined,
		warnings: warnings.length > 0 ? warnings : undefined,
	};
}

function round(n: number, decimals = 2): number {
	const m = 10 ** decimals;
	return Math.round(n * m) / m;
}

function trendFromSlope(
	ys: number[],
	projected: Array<{ y: number }>
): ForecastOutput["trend"] {
	const last = ys[ys.length - 1];
	const future = projected[projected.length - 1].y;
	if (Math.abs(future - last) < Math.abs(last) * 0.02) return "stable";
	return future > last ? "increasing" : "decreasing";
}

function trendFromEndpoints(
	last: number,
	first: number
): ForecastOutput["trend"] {
	if (Math.abs(first - last) < Math.abs(last) * 0.02) return "stable";
	return first > last ? "increasing" : "decreasing";
}

// X-axis extension — try to mirror the cadence of the historical x values
function inferStep(xs: Array<string | number>): { type: "number"; delta: number } | { type: "string" } {
	if (xs.length < 2) return { type: "string" };
	const last = xs[xs.length - 1];
	const prev = xs[xs.length - 2];
	if (typeof last === "number" && typeof prev === "number") {
		return { type: "number", delta: last - prev };
	}
	return { type: "string" };
}

function extendX(
	lastX: string | number,
	step: { type: "number"; delta: number } | { type: "string" },
	offset: number
): string | number {
	if (step.type === "number" && typeof lastX === "number") {
		return lastX + step.delta * offset;
	}
	return `t+${offset}`;
}

// ── Output formatting ────────────────────────────────────────────────────────

export function formatForecast(out: ForecastOutput): string {
	if (!out.found) {
		return `### forecast_series — not available\n${out.message ?? ""}`;
	}

	// Embed a structured envelope at the top so the UI can render an SVG chart.
	// The marker is a single line that the message renderer parses + hides.
	// Claude is instructed in the system prompt to NOT redraw this in ASCII.
	const chartEnvelope = JSON.stringify({
		type: "forecast",
		historical: out.historical ?? [],
		predicted: out.predicted ?? [],
		widget_title: out.widget_title,
		series_name: out.series_name,
		method: out.method_used,
		trend: out.trend,
	});

	const parts: string[] = [];
	parts.push(`<<<chart-data:${chartEnvelope}>>>`);
	parts.push(`### Forecast: ${out.widget_title} → ${out.series_name}`);
	const meta: string[] = [`method=${out.method_used}`, `trend=${out.trend}`];
	if (out.r_squared !== undefined) meta.push(`R²=${out.r_squared}`);
	if (out.mape !== undefined) meta.push(`val MAPE=${out.mape}%`);
	parts.push(meta.join(" · "));

	if (out.warnings) {
		parts.push("");
		out.warnings.forEach((w) => parts.push(`> ⚠️ ${w}`));
	}

	if (out.historical && out.historical.length > 0) {
		parts.push("\n**Historical (last 6)**");
		const tail = out.historical.slice(-6);
		tail.forEach((p) => parts.push(`- ${p.x}: ${p.y}`));
	}

	parts.push("\n**Forecast**");
	parts.push("| Period | Forecast | Lower 95% | Upper 95% |");
	parts.push("|---|---|---|---|");
	for (const p of out.predicted ?? []) {
		parts.push(`| ${p.x} | ${p.y} | ${p.lower} | ${p.upper} |`);
	}

	parts.push(
		"\n_The UI renders this forecast as an interactive chart. Do not re-draw it in ASCII._"
	);

	return parts.join("\n");
}
